import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const ChannelManagerSettings = () => {
  const [loading, setLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeTab, setActiveTab] = useState('channels');

  // Channels State
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);

  // Room Mappings State
  const [roomMappings, setRoomMappings] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [showRoomMappingModal, setShowRoomMappingModal] = useState(false);
  const [newRoomMapping, setNewRoomMapping] = useState({
    roomTypeId: '',
    channelRoomTypeCode: '',
    channelRoomTypeName: ''
  });

  // Rate Plan Mappings State
  const [ratePlanMappings, setRatePlanMappings] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [showRatePlanMappingModal, setShowRatePlanMappingModal] = useState(false);
  const [newRatePlanMapping, setNewRatePlanMapping] = useState({
    ratePlanId: '',
    channelRatePlanCode: '',
    channelRatePlanName: ''
  });

  // Available Channels for Mapping
  const [availableChannels, setAvailableChannels] = useState([
    { id: 1, name: 'Agoda', code: 'agoda' },
    { id: 2, name: 'Booking.com', code: 'booking_com' },
    { id: 3, name: 'Cleartrip', code: 'cleartrip' },
    { id: 4, name: 'EaseMyTrip', code: 'easemytrip' },
    { id: 5, name: 'Expedia', code: 'expedia' },
    { id: 6, name: 'Goibibo', code: 'goibibo' }
  ]);
  const [selectedNewChannel, setSelectedNewChannel] = useState('');

  // Color palette
  const colors = {
    primary: '#4F46E5',
    primaryDark: '#4338CA',
    secondary: '#10B981',
    danger: '#EF4444',
    warning: '#F59E0B',
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827'
    },
    white: '#FFFFFF'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'room-mappings' && selectedChannel) {
      fetchRoomMappings();
    } else if (activeTab === 'rate-plan-mappings' && selectedChannel) {
      fetchRatePlanMappings();
    }
  }, [activeTab, selectedChannel]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      // Fetch channels
      const channelsData = await apiGet('/channel-manager/channels');
      if (channelsData.success) setChannels(channelsData.data);

      // Fetch room types
      const roomTypesData = await apiGet('/channel-manager/room-types');
      if (roomTypesData.success) setRoomTypes(roomTypesData.data);

      // Fetch rate plans
      const ratePlansData = await apiGet('/channel-manager/rate-plans');
      if (ratePlansData.success) setRatePlans(ratePlansData.data);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
    setLoading(false);
  };

  const fetchRoomMappings = async () => {
    try {
      const data = await apiGet('/channel-manager/channels/${selectedChannel.id}/room-mappings');
      if (data.success) setRoomMappings(data.data);
    } catch (error) {
      console.error('Error fetching room mappings:', error);
    }
  };

  const fetchRatePlanMappings = async () => {
    try {
      const data = await apiGet('/channel-manager/channels/${selectedChannel.id}/rate-plan-mappings');
      if (data.success) setRatePlanMappings(data.data);
    } catch (error) {
      console.error('Error fetching rate plan mappings:', error);
    }
  };

  const handleMapNewChannel = async () => {
    if (!selectedNewChannel) {
      alert('Please select a channel to map');
      return;
    }

    const channelToMap = availableChannels.find(c => c.code === selectedNewChannel);

    try {
      const res = await fetch(`${PRODUCTION_URL}/api/channel-manager/channels/map`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channelName: channelToMap.name,
          channelCode: channelToMap.code
        })
      });

      const data = await res.json();
      if (data.success) {
        alert('✅ Channel mapped successfully');
        setSelectedNewChannel('');
        fetchInitialData();
      } else {
        alert('❌ ' + data.message);
      }
    } catch (error) {
      console.error('Error mapping channel:', error);
      alert('❌ Error mapping channel');
    }
  };

  const handleToggleChannel = async (channelId, currentStatus) => {
    try {
      const res = await fetch(`${PRODUCTION_URL}/api/channel-manager/channels/${channelId}/toggle`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      const data = await res.json();
      if (data.success) {
        alert(data.message);
        fetchInitialData();
      } else {
        alert('❌ ' + data.message);
      }
    } catch (error) {
      console.error('Error toggling channel:', error);
      alert('❌ Error updating channel status');
    }
  };

  const handleUpdateCommission = async (channelId, newRate) => {
    try {
      const res = await fetch(`${PRODUCTION_URL}/api/channel-manager/channels/${channelId}/commission`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ commission_rate: newRate })
      });

      const data = await res.json();
      if (data.success) {
        alert('✅ Commission rate updated');
      } else {
        alert('❌ ' + data.message);
      }
    } catch (error) {
      console.error('Error updating commission:', error);
    }
  };

  const handleSaveRoomMapping = async () => {
    if (!newRoomMapping.roomTypeId || !newRoomMapping.channelRoomTypeCode || !newRoomMapping.channelRoomTypeName) {
      alert('Please fill all fields');
      return;
    }

    try {
      const res = await fetch(`${PRODUCTION_URL}/api/channel-manager/channels/${selectedChannel.id}/room-mappings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRoomMapping)
      });

      const data = await res.json();
      if (data.success) {
        alert('✅ Room mapping created successfully');
        setShowRoomMappingModal(false);
        setNewRoomMapping({ roomTypeId: '', channelRoomTypeCode: '', channelRoomTypeName: '' });
        fetchRoomMappings();
      } else {
        alert('❌ ' + data.message);
      }
    } catch (error) {
      console.error('Error saving room mapping:', error);
      alert('❌ Error saving room mapping');
    }
  };

  const handleSaveRatePlanMapping = async () => {
    if (!newRatePlanMapping.ratePlanId || !newRatePlanMapping.channelRatePlanCode || !newRatePlanMapping.channelRatePlanName) {
      alert('Please fill all fields');
      return;
    }

    try {
      const res = await fetch(`${PRODUCTION_URL}/api/channel-manager/channels/${selectedChannel.id}/rate-plan-mappings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRatePlanMapping)
      });

      const data = await res.json();
      if (data.success) {
        alert('✅ Rate plan mapping created successfully');
        setShowRatePlanMappingModal(false);
        setNewRatePlanMapping({ ratePlanId: '', channelRatePlanCode: '', channelRatePlanName: '' });
        fetchRatePlanMappings();
      } else {
        alert('❌ ' + data.message);
      }
    } catch (error) {
      console.error('Error saving rate plan mapping:', error);
      alert('❌ Error saving rate plan mapping');
    }
  };

  const handleDeleteRoomMapping = async (mappingId) => {
    if (!window.confirm('Are you sure you want to delete this room mapping?')) return;

    try {
      const res = await fetch(`${PRODUCTION_URL}/api/channel-manager/room-mappings/${mappingId}`, {
        method: 'DELETE'
      });

      const data = await res.json();
      if (data.success) {
        alert('✅ Room mapping deleted');
        fetchRoomMappings();
      } else {
        alert('❌ ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting room mapping:', error);
      alert('❌ Error deleting room mapping');
    }
  };

  const handleDeleteRatePlanMapping = async (mappingId) => {
    if (!window.confirm('Are you sure you want to delete this rate plan mapping?')) return;

    try {
      const res = await fetch(`${PRODUCTION_URL}/api/channel-manager/rate-plan-mappings/${mappingId}`, {
        method: 'DELETE'
      });

      const data = await res.json();
      if (data.success) {
        alert('✅ Rate plan mapping deleted');
        fetchRatePlanMappings();
      } else {
        alert('❌ ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting rate plan mapping:', error);
      alert('❌ Error deleting rate plan mapping');
    }
  };

  // Common styles
  const styles = {
    container: {
      padding: isMobile ? '16px' : '24px',
      maxWidth: '1600px',
      margin: '0 auto'
    },
    header: {
      marginBottom: isMobile ? '20px' : '32px'
    },
    title: {
      fontSize: isMobile ? '24px' : '32px',
      fontWeight: '700',
      color: colors.gray[900],
      marginBottom: '8px'
    },
    subtitle: {
      fontSize: isMobile ? '14px' : '16px',
      color: colors.gray[600]
    },
    tabsContainer: {
      display: 'flex',
      gap: '8px',
      marginBottom: isMobile ? '16px' : '24px',
      overflowX: 'auto',
      paddingBottom: '8px',
      borderBottom: `2px solid ${colors.gray[200]}`
    },
    tab: (active) => ({
      padding: isMobile ? '10px 16px' : '12px 24px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      color: active ? colors.primary : colors.gray[600],
      background: 'transparent',
      border: 'none',
      borderBottom: active ? `3px solid ${colors.primary}` : '3px solid transparent',
      cursor: 'pointer',
      transition: 'all 0.2s',
      whiteSpace: 'nowrap'
    }),
    card: {
      background: colors.white,
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      padding: isMobile ? '16px' : '24px',
      marginBottom: '16px'
    },
    button: {
      padding: isMobile ? '10px 16px' : '12px 24px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s',
      background: colors.primary,
      color: colors.white
    },
    input: {
      padding: isMobile ? '10px 12px' : '12px 16px',
      fontSize: isMobile ? '13px' : '14px',
      border: `1px solid ${colors.gray[300]}`,
      borderRadius: '8px',
      width: '100%',
      outline: 'none'
    },
    select: {
      padding: isMobile ? '10px 12px' : '12px 16px',
      fontSize: isMobile ? '13px' : '14px',
      border: `1px solid ${colors.gray[300]}`,
      borderRadius: '8px',
      width: '100%',
      outline: 'none',
      background: colors.white,
      cursor: 'pointer'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContent: {
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      maxWidth: '500px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    }
  };

  // Render Channels Tab
  const renderChannelsTab = () => (
    <div>
      {/* Add New Channel */}
      <div style={styles.card}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: colors.gray[900] }}>
          Add New Channel
        </h2>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
              Select Channel
            </label>
            <select
              style={styles.select}
              value={selectedNewChannel}
              onChange={(e) => setSelectedNewChannel(e.target.value)}
            >
              <option value="">Select channel</option>
              {availableChannels.map(ch => (
                <option key={ch.id} value={ch.code}>{ch.name}</option>
              ))}
            </select>
          </div>
          <button style={styles.button} onClick={handleMapNewChannel}>
            + Add Channel
          </button>
        </div>
      </div>

      {/* Existing Channels */}
      <div style={styles.card}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: colors.gray[900] }}>
          Mapped Channels
        </h2>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ background: colors.gray[100], borderBottom: `2px solid ${colors.gray[300]}` }}>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Channel Name</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Status</th>
                {!isMobile && <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Commission Rate</th>}
                {!isMobile && <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Verified</th>}
                <th style={{ padding: '16px', textAlign: 'right', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {channels.length === 0 ? (
                <tr>
                  <td colSpan={isMobile ? 3 : 5} style={{ padding: '40px', textAlign: 'center', color: colors.gray[500] }}>
                    No channels mapped yet
                  </td>
                </tr>
              ) : (
                channels.map((channel) => (
                  <tr key={channel.id} style={{ borderBottom: `1px solid ${colors.gray[200]}` }}>
                    <td style={{ padding: '16px', fontWeight: '500', color: colors.gray[900] }}>
                      {channel.channel_name}
                      {channel.property_id && (
                        <div style={{ fontSize: '12px', color: colors.gray[500], marginTop: '4px' }}>
                          Property: {channel.property_id}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '16px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: channel.is_active ? '#D1FAE5' : '#FEE2E2',
                        color: channel.is_active ? colors.secondary : colors.danger
                      }}>
                        {channel.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {!isMobile && (
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', maxWidth: '120px' }}>
                          <input
                            type="number"
                            value={channel.commission_rate}
                            onChange={(e) => {
                              const newChannels = channels.map(c =>
                                c.id === channel.id ? { ...c, commission_rate: e.target.value } : c
                              );
                              setChannels(newChannels);
                            }}
                            onBlur={(e) => handleUpdateCommission(channel.id, e.target.value)}
                            style={{
                              width: '70px',
                              padding: '6px',
                              borderRadius: '6px',
                              border: `1px solid ${colors.gray[300]}`,
                              fontSize: '13px'
                            }}
                          />
                          <span>%</span>
                        </div>
                      </td>
                    )}
                    {!isMobile && (
                      <td style={{ padding: '16px' }}>
                        {channel.is_verified ? (
                          <span style={{ color: colors.secondary }}>✓ Verified</span>
                        ) : (
                          <span style={{ color: colors.gray[500] }}>- Not verified</span>
                        )}
                      </td>
                    )}
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => {
                            setSelectedChannel(channel);
                            setActiveTab('room-mappings');
                          }}
                          style={{
                            padding: '8px 16px',
                            background: colors.secondary,
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Configure
                        </button>
                        <button
                          onClick={() => handleToggleChannel(channel.id, channel.is_active)}
                          style={{
                            padding: '8px 16px',
                            background: channel.is_active ? colors.gray[400] : colors.secondary,
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          {channel.is_active ? 'Disable' : 'Enable'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Render Room Mappings Tab
  const renderRoomMappingsTab = () => (
    <div>
      {selectedChannel ? (
        <>
          {/* Channel Info */}
          <div style={styles.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: colors.gray[900] }}>
                  Room Mappings for {selectedChannel.channel_name}
                </h2>
                <p style={{ fontSize: '14px', color: colors.gray[600], marginTop: '4px' }}>
                  Map your room types to {selectedChannel.channel_name} room types
                </p>
              </div>
              <button
                style={{ ...styles.button, background: colors.secondary }}
                onClick={() => setShowRoomMappingModal(true)}
              >
                + Add Room Mapping
              </button>
            </div>
          </div>

          {/* Room Mappings Table */}
          <div style={styles.card}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: colors.gray[100], borderBottom: `2px solid ${colors.gray[300]}` }}>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Your Room Type</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Channel Room Code</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Channel Room Name</th>
                    <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {roomMappings.length === 0 ? (
                    <tr>
                      <td colSpan="4" style={{ padding: '40px', textAlign: 'center', color: colors.gray[500] }}>
                        No room mappings configured yet
                      </td>
                    </tr>
                  ) : (
                    roomMappings.map((mapping) => (
                      <tr key={mapping.id} style={{ borderBottom: `1px solid ${colors.gray[200]}` }}>
                        <td style={{ padding: '16px', fontWeight: '500' }}>{mapping.room_type_name}</td>
                        <td style={{ padding: '16px' }}>{mapping.channel_room_type_code}</td>
                        <td style={{ padding: '16px' }}>{mapping.channel_room_type_name}</td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <button
                            onClick={() => handleDeleteRoomMapping(mapping.id)}
                            style={{
                              padding: '6px 16px',
                              background: colors.danger,
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <button
            style={{ ...styles.button, background: colors.gray[400] }}
            onClick={() => {
              setSelectedChannel(null);
              setActiveTab('channels');
            }}
          >
            ← Back to Channels
          </button>
        </>
      ) : (
        <div style={styles.card}>
          <div style={{ textAlign: 'center', padding: '40px', color: colors.gray[500] }}>
            Please select a channel from the Channels tab first
          </div>
        </div>
      )}
    </div>
  );

  // Render Rate Plan Mappings Tab
  const renderRatePlanMappingsTab = () => (
    <div>
      {selectedChannel ? (
        <>
          {/* Channel Info */}
          <div style={styles.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: colors.gray[900] }}>
                  Rate Plan Mappings for {selectedChannel.channel_name}
                </h2>
                <p style={{ fontSize: '14px', color: colors.gray[600], marginTop: '4px' }}>
                  Map your rate plans to {selectedChannel.channel_name} rate plans
                </p>
              </div>
              <button
                style={{ ...styles.button, background: colors.secondary }}
                onClick={() => setShowRatePlanMappingModal(true)}
              >
                + Add Rate Plan Mapping
              </button>
            </div>
          </div>

          {/* Rate Plan Mappings Table */}
          <div style={styles.card}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: colors.gray[100], borderBottom: `2px solid ${colors.gray[300]}` }}>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Your Rate Plan</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Room Type</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Channel Rate Code</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Channel Rate Name</th>
                    <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ratePlanMappings.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: colors.gray[500] }}>
                        No rate plan mappings configured yet
                      </td>
                    </tr>
                  ) : (
                    ratePlanMappings.map((mapping) => (
                      <tr key={mapping.id} style={{ borderBottom: `1px solid ${colors.gray[200]}` }}>
                        <td style={{ padding: '16px', fontWeight: '500' }}>{mapping.plan_name}</td>
                        <td style={{ padding: '16px' }}>{mapping.room_type_name}</td>
                        <td style={{ padding: '16px' }}>{mapping.channel_rate_plan_code}</td>
                        <td style={{ padding: '16px' }}>{mapping.channel_rate_plan_name}</td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <button
                            onClick={() => handleDeleteRatePlanMapping(mapping.id)}
                            style={{
                              padding: '6px 16px',
                              background: colors.danger,
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <button
            style={{ ...styles.button, background: colors.gray[400] }}
            onClick={() => {
              setSelectedChannel(null);
              setActiveTab('channels');
            }}
          >
            ← Back to Channels
          </button>
        </>
      ) : (
        <div style={styles.card}>
          <div style={{ textAlign: 'center', padding: '40px', color: colors.gray[500] }}>
            Please select a channel from the Channels tab first
          </div>
        </div>
      )}
    </div>
  );

  return (
    <AdminLayout>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>Channel Manager - Settings</h1>
          <p style={styles.subtitle}>Configure OTA channels, room mappings, and rate plan mappings</p>
        </div>

        {/* Tabs */}
        <div style={styles.tabsContainer}>
          <button style={styles.tab(activeTab === 'channels')} onClick={() => setActiveTab('channels')}>
            🔗 Channels
          </button>
          <button style={styles.tab(activeTab === 'room-mappings')} onClick={() => setActiveTab('room-mappings')}>
            🏨 Room Mappings
          </button>
          <button style={styles.tab(activeTab === 'rate-plan-mappings')} onClick={() => setActiveTab('rate-plan-mappings')}>
            💰 Rate Plan Mappings
          </button>
        </div>

        {/* Tab Content */}
        {loading ? (
          <div style={{
            ...styles.card,
            textAlign: 'center',
            padding: '60px 20px',
            color: colors.gray[500]
          }}>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>⏳</div>
            <div>Loading...</div>
          </div>
        ) : (
          <>
            {activeTab === 'channels' && renderChannelsTab()}
            {activeTab === 'room-mappings' && renderRoomMappingsTab()}
            {activeTab === 'rate-plan-mappings' && renderRatePlanMappingsTab()}
          </>
        )}
      </div>

      {/* Room Mapping Modal */}
      {showRoomMappingModal && (
        <div style={styles.modal} onClick={() => setShowRoomMappingModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px' }}>
              Add Room Mapping
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Your Room Type
              </label>
              <select
                style={styles.select}
                value={newRoomMapping.roomTypeId}
                onChange={(e) => setNewRoomMapping({ ...newRoomMapping, roomTypeId: e.target.value })}
              >
                <option value="">Select room type</option>
                {roomTypes.map(rt => (
                  <option key={rt.id} value={rt.id}>{rt.room_type_name}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Channel Room Type Code
              </label>
              <input
                type="text"
                style={styles.input}
                value={newRoomMapping.channelRoomTypeCode}
                onChange={(e) => setNewRoomMapping({ ...newRoomMapping, channelRoomTypeCode: e.target.value })}
                placeholder="e.g., DLX"
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Channel Room Type Name
              </label>
              <input
                type="text"
                style={styles.input}
                value={newRoomMapping.channelRoomTypeName}
                onChange={(e) => setNewRoomMapping({ ...newRoomMapping, channelRoomTypeName: e.target.value })}
                placeholder="e.g., Deluxe Room"
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                style={{ ...styles.button, background: colors.gray[400], flex: 1 }}
                onClick={() => {
                  setShowRoomMappingModal(false);
                  setNewRoomMapping({ roomTypeId: '', channelRoomTypeCode: '', channelRoomTypeName: '' });
                }}
              >
                Cancel
              </button>
              <button
                style={{ ...styles.button, flex: 1 }}
                onClick={handleSaveRoomMapping}
              >
                Save Mapping
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rate Plan Mapping Modal */}
      {showRatePlanMappingModal && (
        <div style={styles.modal} onClick={() => setShowRatePlanMappingModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px' }}>
              Add Rate Plan Mapping
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Your Rate Plan
              </label>
              <select
                style={styles.select}
                value={newRatePlanMapping.ratePlanId}
                onChange={(e) => setNewRatePlanMapping({ ...newRatePlanMapping, ratePlanId: e.target.value })}
              >
                <option value="">Select rate plan</option>
                {ratePlans.map(rp => (
                  <option key={rp.id} value={rp.id}>{rp.plan_name} ({rp.plan_code})</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Channel Rate Plan Code
              </label>
              <input
                type="text"
                style={styles.input}
                value={newRatePlanMapping.channelRatePlanCode}
                onChange={(e) => setNewRatePlanMapping({ ...newRatePlanMapping, channelRatePlanCode: e.target.value })}
                placeholder="e.g., BB"
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Channel Rate Plan Name
              </label>
              <input
                type="text"
                style={styles.input}
                value={newRatePlanMapping.channelRatePlanName}
                onChange={(e) => setNewRatePlanMapping({ ...newRatePlanMapping, channelRatePlanName: e.target.value })}
                placeholder="e.g., Bed & Breakfast"
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                style={{ ...styles.button, background: colors.gray[400], flex: 1 }}
                onClick={() => {
                  setShowRatePlanMappingModal(false);
                  setNewRatePlanMapping({ ratePlanId: '', channelRatePlanCode: '', channelRatePlanName: '' });
                }}
              >
                Cancel
              </button>
              <button
                style={{ ...styles.button, flex: 1 }}
                onClick={handleSaveRatePlanMapping}
              >
                Save Mapping
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default ChannelManagerSettings;